import React, { useEffect, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@components/Common';
import { FormContainer } from '@components/Common/Loaders/FormLoader';
import {
  BookkeepingEntry,
  entryStatus,
  entrySource,
  ActionDirection,
  ShipmentType,
} from '@api/queries/inventory';
import { useTerminalsQuery } from '@api/queries/terminals';
import { useTanksQuery } from '@api/queries/tanks';
import { useCustomersQuery } from '@api/queries/customers';
import { useProductsQuery } from '@api/queries/products';
import { useProductMixturesQuery } from '@api/queries/productMixtures';
import { useAuth } from '@components/MsalProvider';
import { EntryType } from '~types/types';
import { nowIsoUtc } from '@helpers/dateUtils';
import { EditorMode } from './hooks/useBookkeepingEntry';

// Sections
import BasicInfoSection from './sections/BasicInfoSection';
import ShipmentSection from './sections/ShipmentSection';
import NotesSection from './sections/NotesSection';
import ExternalDataSection from './sections/ExternalDataSection';
import FormActions from './components/FormActions';

const Columns = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;

  > div.column {
    box-sizing: border-box;
    width: 25%;
    padding-right: 15px;

    &.wide {
      flex-grow: 1;
    }

    &:last-child {
      padding-right: 0;
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export interface BookkeepingFormData extends BookkeepingEntry {}

interface Props {
  entry?: BookkeepingEntry;
  mode: EditorMode;
  isSaving: boolean;
  isDeleting: boolean;
  hasNextUnconfirmed: boolean;
  onSave: (entry: BookkeepingEntry) => Promise<void>;
  onSaveAndNext: (entry: BookkeepingEntry) => Promise<void>;
  onDelete: () => Promise<void>;
  onClose: () => void;
}

// ============================================================================
// Default values for new entry
// ============================================================================

const createDefaultEntry = (userTerminals: string[]): BookkeepingFormData => ({
  id: uuidv4(),
  terminalId: userTerminals.length > 0 ? userTerminals[0] : null,
  actionDirection: ActionDirection.Outgoing,
  tankId: null,
  customerId: null,
  productId: null,
  productAliasId: null,
  entryType: EntryType.Product,
  shipmentType: ShipmentType.Truck,
  shipmentTypeName: 'Truck',
  unloadingStarted: null,
  unloadingFinished: null,
  unloadingStartedTimeSelected: false,
  unloadingFinishedTimeSelected: false,
  amount: null,
  truck: null,
  ship: null,
  pipe: null,
  train: null,
  containerTrain: null,
  containerTruck: null,
  notes: '',
  billingInformation: '',
  entryState: entryStatus.incomplete,
  source: entrySource.User,
  created: nowIsoUtc(),
  createdBy: '',
  modified: null,
  modifiedBy: null,
  approvedBy: null,
  approved: null,
  externalData: null,
  productMixtureId: null,
  order: null,
  concentrationPercent: null,
  sendToCustomer: true,
  lastSentToCustomer: null,
});

// ============================================================================
// Component
// ============================================================================

const BookkeepingEntryForm: React.FC<Props> = ({
  entry,
  mode,
  isSaving,
  isDeleting,
  hasNextUnconfirmed,
  onSave,
  onSaveAndNext,
  onDelete,
  onClose,
}) => {
  const { t } = useTranslation();

  // Get user terminals from auth context
  const { terminals: userTerminals } = useAuth();

  // Fetch reference data
  const { data: terminals = [] } = useTerminalsQuery();
  const { data: tanks = [] } = useTanksQuery();
  const { data: customers = [] } = useCustomersQuery();
  const { data: products = [] } = useProductsQuery();
  const { data: productMixtures = [] } = useProductMixturesQuery();

  // Create default values
  const defaultValues = useMemo(() => {
    if (entry) {
      return entry;
    }
    return createDefaultEntry(userTerminals);
  }, [entry, userTerminals]);

  // Setup form
  const methods = useForm<BookkeepingFormData>({
    defaultValues,
    mode: 'onChange',
  });

  const { handleSubmit, watch, reset } = methods;

  // Reset form when entry changes (e.g., navigating to different entry)
  useEffect(() => {
    if (entry) {
      reset(entry);
    }
  }, [entry, reset]);

  // Watch entry type for conditional rendering
  const entryType = watch('entryType');
  const entryState = watch('entryState');
  const tankId = watch('tankId');
  const productMixtureId = watch('productMixtureId');
  const isProductMixture = entryType === EntryType.Mixture;
  const isOtherEntry = entryType === EntryType.Other;
  const isLocked = mode === 'approved' || mode === 'readonly';

  // For new entries, require selection before showing full form:
  // - Product entries: require tank selection
  // - Mixture entries: require product mixture selection
  // - Other entries: no restrictions
  const isNewEntry = mode === 'new';
  const hasRequiredSelection = isOtherEntry ||
    (!isProductMixture && !!tankId) ||
    (isProductMixture && !!productMixtureId);
  const showFullForm = !isNewEntry || hasRequiredSelection;

  // Form submission
  const onSubmit = async (data: BookkeepingFormData) => {
    await onSave(data);
  };

  const onSubmitAndNext = async (data: BookkeepingFormData) => {
    await onSaveAndNext(data);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown}>
        <FormContainer>
          <Columns>
            <BasicInfoSection
              mode={mode}
              terminals={terminals}
              tanks={tanks}
              customers={customers}
              products={products}
              productMixtures={productMixtures}
              isLocked={isLocked}
              isProductMixture={isProductMixture}
            />
          </Columns>

          <hr />

          <Columns>
            {!isOtherEntry && showFullForm && (
              <ShipmentSection
                mode={mode}
                tanks={tanks}
                products={products}
                isLocked={isLocked}
                isProductMixture={isProductMixture}
              />
            )}
            {!isOtherEntry && !showFullForm && (
              <div className="column" style={{ color: '#666', fontStyle: 'italic' }}>
                <p>{isProductMixture ? t('entry.select_mixture_first') : t('entry.select_tank_first')}</p>
                <Button type="button" secondary={true} onClick={onClose}>
                  {t('entry.close_without_save')}
                </Button>
              </div>
            )}

            {showFullForm && (
              <div className="wide column">
                <NotesSection
                  mode={mode}
                  isLocked={isLocked}
                  customers={customers}
                />

                <FormActions
                  mode={mode}
                  isSaving={isSaving}
                  isDeleting={isDeleting}
                  hasNextUnconfirmed={hasNextUnconfirmed}
                  entryState={entryState}
                  isOtherEntry={isOtherEntry}
                  onSave={handleSubmit(onSubmit)}
                  onSaveAndNext={handleSubmit(onSubmitAndNext)}
                  onDelete={onDelete}
                  onClose={onClose}
                />

                <ExternalDataSection />
              </div>
            )}
          </Columns>
        </FormContainer>
      </form>
    </FormProvider>
  );
};

export default BookkeepingEntryForm;
